/**
 * UnlockApprovalWorkflow - Premium animated component for manager unlock approval
 * 
 * Features:
 * - Premium interactive micro-animations with golden ratio timing
 * - Glass morphism design with floating backgrounds
 * - Question exposure analysis with visual indicators
 * - Smart replacement question interface
 * - Cinematic UI experience with staggered choreography
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogContent,
  Chip,
  Alert,
  Grid,
  Divider,
  CircularProgress,
  keyframes,
  Fade,
  Grow,
  IconButton,
} from '@mui/material';
import {
  LockOpen,
  CheckCircle,
  Warning,
  Close,
  Visibility,
  Edit,
  ContentCopy,
  Send,
  Person,
  Schedule,
  QuestionAnswer,
} from '@mui/icons-material';
import { unlockAPI, questionAPI, Question } from '../services/apiService';

// Golden ratio constant (φ = phi)
const PHI = 1.618034;

// Premium animation patterns matching design language
const floatingGradient = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-10px) rotate(1deg); }
  66% { transform: translateY(5px) rotate(-0.5deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const borderShimmer = keyframes`
  0% { border-color: rgba(255,255,255,0.3); }
  50% { border-color: rgba(33,150,243,0.4); }
  100% { border-color: rgba(255,255,255,0.3); }
`;

const textReveal = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Golden ratio timing constants
const TIMING = {
  fast: `${PHI * 0.1}s`,      // 161.8ms
  medium: `${PHI * 0.162}s`,  // 261.8ms  
  slow: `${PHI * 0.424}s`,    // 685.2ms
  pulse: `${PHI * 2}s`,       // 3.236s
  border: `${PHI * 2}s`,      // 3.236s
  float: '20s',               // Background motion
  shimmer: `${PHI}s`,         // 1.618s
};

interface UnlockApprovalWorkflowProps {
  interviewUuid: string;
  testUuid: string;
  candidateName: string;
  candidateEmail: string;
  testName: string;
  unlockReason: string;
  requestedAt: string;
  onClose: () => void;
  onApprovalComplete: (newInterviewUuid?: string) => void;
}

interface UnlockStatus {
  status: 'none' | 'pending' | 'approved' | 'denied';
  unlock_requested: boolean;
  unlock_approved: boolean;
  requested_at?: string;
  approved_at?: string;
  questions_seen_ids: string[];
  questions_seen_count: number;
}

interface QuestionReplacement {
  questionId: string;
  originalContent: string;
  newContent: string;
  isReplaced: boolean;
}

const UnlockApprovalWorkflow: React.FC<UnlockApprovalWorkflowProps> = ({
  interviewUuid,
  testUuid,
  candidateName,
  candidateEmail,
  testName,
  unlockReason,
  requestedAt,
  onClose,
  onApprovalComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [unlockStatus, setUnlockStatus] = useState<UnlockStatus | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionReplacements, setQuestionReplacements] = useState<QuestionReplacement[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load unlock status and questions
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get unlock status with questions seen
        const status = await unlockAPI.getUnlockStatus(interviewUuid);
        setUnlockStatus(status);

        // Get all questions for the test
        const allQuestions = await questionAPI.getByTestUuid(testUuid);
        
        // Filter to only show questions that the candidate has seen
        const seenQuestions = allQuestions.filter(question => 
          status.questions_seen_ids?.includes(question.id) || false
        );

        setQuestions(seenQuestions);

        // Initialize question replacements
        const replacements: QuestionReplacement[] = seenQuestions.map((question) => ({
          questionId: question.id,
          originalContent: question.content,
          newContent: '',
          isReplaced: false,
        }));

        setQuestionReplacements(replacements);

      } catch (err) {
        console.error('Failed to load unlock data:', err);
        setError('Failed to load unlock request details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [interviewUuid, testUuid]);

  const handleQuestionReplacementChange = (questionId: string, newContent: string) => {
    setQuestionReplacements(prev => 
      prev.map(replacement => 
        replacement.questionId === questionId 
          ? { ...replacement, newContent, isReplaced: newContent.trim().length > 0 }
          : replacement
      )
    );
  };

  const handleApproveOriginal = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await unlockAPI.approveUnlock(interviewUuid);
      
      setSuccess('Interview unlocked successfully! Candidate can now restart with original questions.');
      
      setTimeout(() => {
        onApprovalComplete();
      }, 2000);

    } catch (err) {
      console.error('Failed to approve unlock:', err);
      setError('Failed to approve unlock request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloneWithNewQuestions = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Prepare replacement questions (only those that have new content)
      const replacements = questionReplacements
        .filter(replacement => replacement.isReplaced)
        .map(replacement => ({
          old_question_id: replacement.questionId,
          new_content: replacement.newContent,
        }));

      if (replacements.length === 0) {
        setError('Please provide replacement content for at least one question.');
        return;
      }

      const result = await unlockAPI.cloneInterview(interviewUuid, replacements);
      
      setSuccess(`New interview created successfully! Replaced ${result.questions_replaced} questions. Invitation sent to candidate.`);
      
      setTimeout(() => {
        onApprovalComplete(result.new_interview_uuid);
      }, 2000);

    } catch (err) {
      console.error('Failed to clone interview:', err);
      setError('Failed to create new interview. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} maxWidth="md" fullWidth>
        <DialogContent sx={{ 
          textAlign: 'center', 
          py: PHI * 4,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
        }}>
          <CircularProgress sx={{ 
            mb: 2,
            animation: `${pulse} ${TIMING.pulse} ease-in-out infinite`,
          }} />
          <Typography variant="h6">Loading unlock request details...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={true} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
          backdropFilter: 'blur(25px)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: PHI,
          animation: `${floatingGradient} ${TIMING.float} ease-in-out infinite, ${borderShimmer} ${TIMING.border} ease-in-out infinite`,
          contain: 'layout style paint',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          boxShadow: '0 25px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1) inset',
        }
      }}
    >
      <DialogContent sx={{ p: PHI * 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: PHI * 2 }}>
          <LockOpen 
            sx={{ 
              fontSize: PHI * 20,
              mr: PHI, 
              color: 'primary.main',
              animation: `${pulse} ${TIMING.pulse} ease-in-out infinite`,
            }} 
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="h4" 
              fontWeight="bold"
              sx={{ 
                color: '#1a1a1a',
                textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                animation: `${textReveal} ${TIMING.slow} ease-out ${TIMING.fast} both`,
              }}
            >
              Interview Unlock Request
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#4a4a4a',
                animation: `${textReveal} ${TIMING.slow} ease-out ${TIMING.medium} both`,
              }}
            >
              Review candidate request and approve unlock strategy
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ 
              animation: `${textReveal} ${TIMING.slow} ease-out ${TIMING.slow} both`,
              '&:hover': {
                transform: 'translateY(-2px) scale(1.05)',
                transition: `transform ${TIMING.fast} ease`,
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Candidate Information */}
        <Card sx={{
          mb: PHI * 2,
          background: 'linear-gradient(135deg, rgba(33,150,243,0.1), rgba(33,150,243,0.05))',
          border: '1px solid rgba(33,150,243,0.2)',
          borderRadius: PHI / 2,
          animation: `${textReveal} ${TIMING.slow} ease-out 0.6180034s both`,
        }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Person sx={{ fontSize: PHI * 16, color: 'primary.main' }} />
              </Grid>
              <Grid item xs>
                <Typography variant="h6" fontWeight="bold">{candidateName}</Typography>
                <Typography variant="body2" color="textSecondary">{candidateEmail}</Typography>
                <Typography variant="body2" color="textSecondary">Test: {testName}</Typography>
              </Grid>
              <Grid item>
                <Chip 
                  icon={<Schedule />}
                  label={`Requested ${new Date(requestedAt).toLocaleDateString()}`}
                  size="small"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Unlock Reason */}
        <Card sx={{
          mb: PHI * 2,
          background: 'linear-gradient(135deg, rgba(255,152,0,0.1), rgba(255,152,0,0.05))',
          border: '1px solid rgba(255,152,0,0.2)',
          borderRadius: PHI / 2,
          animation: `${textReveal} ${TIMING.slow} ease-out 0.8090034s both`,
        }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <QuestionAnswer sx={{ mr: 1 }} />
              Reason for Unlock Request
            </Typography>
            <Typography variant="body1" sx={{ 
              p: 2, 
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: PHI / 4,
              fontStyle: 'italic',
            }}>
              "{unlockReason}"
            </Typography>
          </CardContent>
        </Card>

        {/* Question Exposure Analysis */}
        {unlockStatus && (
          <Card sx={{
            mb: PHI * 2,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: PHI / 2,
            animation: `${textReveal} ${TIMING.slow} ease-out 1.0910034s both`,
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Visibility sx={{ mr: 1 }} />
                Question Exposure Analysis
              </Typography>
              
              <Alert 
                severity={unlockStatus.questions_seen_count > 0 ? "warning" : "success"}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  Candidate viewed {unlockStatus.questions_seen_count} out of {questions.length} questions
                </Typography>
                <Typography variant="body2">
                  {unlockStatus.questions_seen_count === 0 
                    ? "No questions compromised - safe to restart with original questions"
                    : "Questions compromised - recommend creating new interview with replacement questions"
                  }
                </Typography>
              </Alert>

              {unlockStatus.questions_seen_count > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Replace Compromised Questions:
                  </Typography>
                  
                  {questionReplacements.map((replacement, index) => (
                    <Grow key={replacement.questionId} in={true} timeout={(index + 1) * 200}>
                      <Card sx={{
                        mb: 2,
                        background: 'linear-gradient(135deg, rgba(244,67,54,0.1), rgba(244,67,54,0.05))',
                        border: '1px solid rgba(244,67,54,0.2)',
                        borderRadius: PHI / 4,
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Warning sx={{ mr: 1, color: 'warning.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">
                              Question {index + 1} (Compromised)
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Original: {replacement.originalContent}
                          </Typography>
                          
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Replacement Question Content"
                            placeholder="Enter new question content to replace the compromised question..."
                            value={replacement.newContent}
                            onChange={(e) => handleQuestionReplacementChange(replacement.questionId, e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  transform: 'translateY(-1px) scale(1.002)',
                                  transition: `transform ${TIMING.fast} ease`,
                                },
                                '&.Mui-focused': {
                                  transform: 'translateY(-2px) scale(1.005)',
                                  transition: `transform ${TIMING.medium} ease`,
                                },
                              },
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grow>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              px: PHI * 2,
              py: PHI,
              borderRadius: PHI / 2,
              '&:hover': {
                transform: 'translateY(-2px) scale(1.02)',
                transition: `transform ${TIMING.fast} ease`,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="warning"
            startIcon={<CheckCircle />}
            onClick={handleApproveOriginal}
            disabled={isSubmitting}
            sx={{
              px: PHI * 2,
              py: PHI,
              borderRadius: PHI / 2,
              background: 'linear-gradient(135deg, #ff9800, #f57c00)',
              '&:hover': {
                transform: 'translateY(-2px) scale(1.02)',
                transition: `transform ${TIMING.fast} ease`,
                boxShadow: '0 8px 24px rgba(255,152,0,0.4)',
              },
            }}
          >
            {isSubmitting ? 'Approving...' : 'Approve Original Interview'}
          </Button>

          {unlockStatus && unlockStatus.questions_seen_count > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ContentCopy />}
              onClick={handleCloneWithNewQuestions}
              disabled={isSubmitting || questionReplacements.filter(r => r.isReplaced).length === 0}
              sx={{
                px: PHI * 2,
                py: PHI,
                borderRadius: PHI / 2,
                background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                animation: questionReplacements.filter(r => r.isReplaced).length > 0 
                  ? `${shimmer} ${TIMING.shimmer} infinite` 
                  : 'none',
                '&:hover:not(:disabled)': {
                  transform: 'translateY(-2px) scale(1.02)',
                  transition: `transform ${TIMING.fast} ease`,
                  boxShadow: '0 8px 24px rgba(25,118,210,0.4)',
                },
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create New Interview'}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UnlockApprovalWorkflow;